import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from '../../../../services/notificationService';
import {
  timeEntryApi, TimeEntryDto,
  ClientDto, ProjectDto,
} from 'api/services/timeTrackerApi';
import { toDecimalHours, fromDecimalHours, formatHM } from './duration';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

interface AdhocLogModalProps {
  open: boolean;
  onClose: () => void;
  clients: ClientDto[];
  projects: ProjectDto[];
  /** create (default) = quick-log flow with recents; edit = pre-populate + save-once. */
  mode?: 'create' | 'edit';
  /** Entry to edit — required when mode === 'edit'. Must be an ad-hoc entry (taskId is null). */
  editing?: TimeEntryDto | null;
  /** Called after a successful create or edit — MyTimesheetScreen refetches on this. */
  onSaved?: () => void;
  // Minimize wiring — pass tabId + moduleName + onRestoreModal from the parent screen.
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit', editing?: TimeEntryDto | null) => void;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

const AdhocLogModal: React.FC<AdhocLogModalProps> = ({
  open, onClose, clients, projects,
  mode = 'create', editing = null, onSaved,
  tabId, moduleName, onRestoreModal, minimizeRef,
}) => {
  const isEdit = mode === 'edit';
  const { minimize } = useModalMinimize();
  // Form state
  const [clientId, setClientId]   = useState('');
  const [projectId, setProjectId] = useState('');
  const [entryDate, setEntryDate] = useState(todayIso());
  const [hours, setHours]         = useState<number>(1);
  const [minutes, setMinutes]     = useState<number>(0);
  const [narration, setNarration] = useState('');
  const [notes, setNotes]         = useState('');
  const [saving, setSaving]       = useState(false);
  const [errors, setErrors]       = useState<Record<string, string>>({});

  // Recent ad-hoc entries
  const [recents, setRecents] = useState<TimeEntryDto[]>([]);
  const [loadingRecents, setLoadingRecents] = useState(false);

  // Delete confirmation
  const [pendingDelete, setPendingDelete] = useState<TimeEntryDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Discard-changes confirmation
  const [discardOpen, setDiscardOpen] = useState(false);
  const initialSnapshotRef = useRef<string>('');
  const currentSnapshot = useMemo(
    () => JSON.stringify({ clientId, projectId, entryDate, hours, minutes, narration, notes }),
    [clientId, projectId, entryDate, hours, minutes, narration, notes]
  );
  const isDirty = initialSnapshotRef.current !== '' && currentSnapshot !== initialSnapshotRef.current;

  // Minimize plumbing — refs so handleMinimize gets current values from stale closures
  const modeRef    = useRef(mode);    modeRef.current    = mode;
  const editingRef = useRef(editing); editingRef.current = editing;
  const stateRef   = useRef({ clientId, projectId, entryDate, hours, minutes, narration, notes });
  stateRef.current = { clientId, projectId, entryDate, hours, minutes, narration, notes };

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentMode = modeRef.current;
    const currentEditing = editingRef.current;
    const currentState = { ...stateRef.current };
    const snapshot = initialSnapshotRef.current;
    const isDirtyNow = snapshot ? JSON.stringify(currentState) !== snapshot : false;

    const modalId = currentMode === 'edit' && currentEditing
      ? `${tabId}-form-modal-adhoc-edit-${currentEditing.id}`
      : `${tabId}-form-modal-adhoc-create-1`;
    const narrationStr = currentState.narration.trim();
    const title = currentMode === 'edit'
      ? `Edit ad-hoc - ${currentEditing?.entryDate?.slice(0, 10) ?? ''}`
      : (narrationStr ? `Quick Log - ${narrationStr.slice(0, 40)}` : `Quick Log (1)`);

    minimize({
      id: modalId, title, moduleName, tabId, isDirty: isDirtyNow,
      onRestore: () => {
        // Ask parent to re-open in the right mode; parent will pass `editing` back.
        onRestoreModal?.(currentMode, currentEditing);
        // Push the saved field values back once the effect fires.
        // The parent will set open=true, then this component's populate effect
        // will run — we need to bypass it and restore the captured state.
        // Since we can't call setters here (they'd be captured stale) we set
        // them synchronously via microtask; the effect runs after and will
        // see initialSnapshotRef seeded below.
        queueMicrotask(() => {
          setClientId(currentState.clientId);
          setProjectId(currentState.projectId);
          setEntryDate(currentState.entryDate);
          setHours(currentState.hours);
          setMinutes(currentState.minutes);
          setNarration(currentState.narration);
          setNotes(currentState.notes);
          initialSnapshotRef.current = snapshot;
        });
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const clientOptions = useMemo(
    () => clients.map(c => ({ value: c.id, label: c.name })),
    [clients]
  );
  const projectOptions = useMemo(
    () => projects.map(p => ({ value: p.id, label: p.name })),
    [projects]
  );

  const resetForm = useCallback(() => {
    setClientId('');
    setProjectId('');
    setEntryDate(todayIso());
    setHours(1);
    setMinutes(0);
    setNarration('');
    setNotes('');
    setErrors({});
  }, []);

  const loadRecents = useCallback(async () => {
    setLoadingRecents(true);
    try {
      const items = await timeEntryApi.listMyAdhoc(20);
      setRecents(items);
    } catch {
      // Non-fatal — the form still works even if the list can't load.
    } finally {
      setLoadingRecents(false);
    }
  }, []);

  // Populate form on open; refresh recents only in create mode.
  useEffect(() => {
    if (!open) return;
    if (isEdit && editing) {
      const { h, m } = fromDecimalHours(editing.hours);
      const seed = {
        clientId: editing.clientId ?? '',
        projectId: editing.projectId ?? '',
        entryDate: editing.entryDate?.slice(0, 10) || todayIso(),
        hours: h,
        minutes: m,
        narration: editing.narration ?? '',
        notes: editing.notes ?? '',
      };
      setClientId(seed.clientId);
      setProjectId(seed.projectId);
      setEntryDate(seed.entryDate);
      setHours(seed.hours);
      setMinutes(seed.minutes);
      setNarration(seed.narration);
      setNotes(seed.notes);
      setErrors({});
      initialSnapshotRef.current = JSON.stringify(seed);
    } else {
      resetForm();
      loadRecents();
      initialSnapshotRef.current = JSON.stringify({
        clientId: '', projectId: '', entryDate: todayIso(),
        hours: 1, minutes: 0, narration: '', notes: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, editing?.id]);

  const handleClose = useCallback(() => {
    if (saving) return;
    if (isDirty) { setDiscardOpen(true); return; }
    onClose();
  }, [saving, isDirty, onClose]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    const decimal = toDecimalHours(hours, minutes);
    if (!narration.trim())     errs.narration = 'Narration is required';
    if (!entryDate)            errs.entryDate = 'Date is required';
    if (decimal <= 0)          errs.duration  = 'Enter at least 1 minute';
    else if (decimal > 24)     errs.duration  = 'Cannot exceed 24 hours';

    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    try {
      if (isEdit && editing) {
        await timeEntryApi.update(editing.id, {
          entryDate,
          hours: decimal,
          notes: notes || null,
          narration: narration.trim(),
          clientId: clientId || null,
          projectId: projectId || null,
          rowVersion: editing.rowVersion,
        });
        notificationService.toast.success('Time entry updated.');
        onSaved?.();
        onClose();
      } else {
        await timeEntryApi.createAdhoc({
          entryDate,
          hours: decimal,
          narration: narration.trim(),
          clientId: clientId || null,
          projectId: projectId || null,
          notes: notes || null,
        });
        notificationService.toast.success('Time entry logged.');
        // Keep modal open so users can log several in a row; reset only the
        // duration/narration/notes so client & date carry over.
        setHours(1);
        setMinutes(0);
        setNarration('');
        setNotes('');
        setErrors({});
        // Re-baseline the snapshot so leftover client/date don't count as dirty.
        initialSnapshotRef.current = JSON.stringify({
          clientId, projectId, entryDate,
          hours: 1, minutes: 0, narration: '', notes: '',
        });
        await loadRecents();
        onSaved?.();
      }
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || (isEdit ? 'Update failed.' : 'Log failed.'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await timeEntryApi.remove(pendingDelete.id, pendingDelete.rowVersion);
      setPendingDelete(null);
      await loadRecents();
      notificationService.toast.success('Time entry deleted.');
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        className="modal-backdrop"
        onClick={handleClose}
      >
        <div
          className="modal-container modal-md"
          onClick={e => e.stopPropagation()}
          style={{ maxWidth: 640, width: '95%' }}
        >
          <div className="modal-header" style={{ minHeight: 'auto' }}>
            <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <IconSystem.clock size={16} />
              {isEdit ? 'Edit Time Entry' : 'Quick Log'}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              {tabId && moduleName && (
                <button
                  className="modal-header-btn"
                  onClick={handleMinimize}
                  title="Minimize to bar"
                  disabled={saving}
                >
                  <IconSystem.chevronDown size={16} />
                </button>
              )}
              <button
                className="modal-close"
                onClick={handleClose}
                title="Close"
              >
                <IconSystem.x size={18} />
              </button>
            </div>
          </div>

          <div className="modal-content" style={{ padding: '1rem 1.25rem' }}>
            <form onSubmit={submit}>
              <div className="form-grid-2">
                <div className="form-group" data-field="clientId">
                  <SyutSelect
                    label="Client"
                    options={clientOptions}
                    value={clientId}
                    onChange={(val) => setClientId((val as string) || '')}
                    placeholder="— None —"
                    isClearable
                    size="sm"
                  />
                </div>
                <div className="form-group" data-field="projectId">
                  <SyutSelect
                    label="Project"
                    options={projectOptions}
                    value={projectId}
                    onChange={(val) => setProjectId((val as string) || '')}
                    placeholder="— None —"
                    isClearable
                    size="sm"
                  />
                </div>
              </div>

              <div className="form-grid-2" style={{ marginTop: '0.75rem' }}>
                <div className="form-group" data-field="entryDate">
                  <label className="form-label">
                    Date<span style={{ color: 'var(--danger)' }}> *</span>
                  </label>
                  <input
                    type="date"
                    className={`form-input ${errors.entryDate ? 'form-field-error' : ''}`}
                    value={entryDate}
                    onChange={(e) => setEntryDate(e.target.value)}
                  />
                  {errors.entryDate && <div className="form-error-message">{errors.entryDate}</div>}
                </div>
                <div className="form-group" data-field="duration">
                  <label className="form-label">
                    Duration<span style={{ color: 'var(--danger)' }}> *</span>
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input
                      type="number"
                      className={`form-input ${errors.duration ? 'form-field-error' : ''}`}
                      style={{ width: 70 }}
                      min={0} max={23} step={1}
                      value={hours}
                      onChange={(e) => setHours(Math.max(0, Math.min(23, Number(e.target.value) || 0)))}
                      aria-label="Hours"
                    />
                    <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>h</span>
                    <input
                      type="number"
                      className={`form-input ${errors.duration ? 'form-field-error' : ''}`}
                      style={{ width: 70 }}
                      min={0} max={59} step={5}
                      value={minutes}
                      onChange={(e) => setMinutes(Math.max(0, Math.min(59, Number(e.target.value) || 0)))}
                      aria-label="Minutes"
                    />
                    <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>m</span>
                  </div>
                  {errors.duration && <div className="form-error-message">{errors.duration}</div>}
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '0.75rem' }} data-field="narration">
                <label className="form-label">
                  Narration<span style={{ color: 'var(--danger)' }}> *</span>
                </label>
                <textarea
                  className={`form-textarea ${errors.narration ? 'form-field-error' : ''}`}
                  rows={3}
                  value={narration}
                  onChange={(e) => setNarration(e.target.value)}
                  maxLength={500}
                  placeholder="What did you work on?"
                />
                {errors.narration && <div className="form-error-message">{errors.narration}</div>}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.75rem' }}>
                <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={saving}>
                  Close
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? (isEdit ? 'Saving…' : 'Logging…') : (isEdit ? 'Save changes' : 'Log time')}
                </button>
              </div>
            </form>

            {/* Recent ad-hoc entries — quick review + undo (create mode only) */}
            {!isEdit && (
            <div style={{
              marginTop: '1.25rem',
              borderTop: '1px solid var(--border-color)',
              paddingTop: '0.75rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <IconSystem.list size={14} color="var(--primary)" />
                <strong style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>Recent ad-hoc entries</strong>
                {loadingRecents && <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Loading…</span>}
              </div>
              {recents.length === 0 && !loadingRecents && (
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: '0.5rem 0' }}>
                  No ad-hoc entries yet.
                </div>
              )}
              {recents.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', maxHeight: 200, overflowY: 'auto' }}>
                  {recents.map(r => (
                    <div key={r.id} style={{
                      display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                      padding: '0.375rem 0.5rem',
                      borderRadius: '0.375rem',
                      background: 'var(--bg-secondary)',
                      fontSize: '0.8rem',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
                          <strong style={{ color: 'var(--primary)' }}>{formatHM(r.hours)}</strong>
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                            {r.entryDate?.slice(0, 10)}
                          </span>
                          {(r.clientName || r.projectName) && (
                            <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                              {r.clientName ?? ''}{r.clientName && r.projectName ? ' · ' : ''}{r.projectName ?? ''}
                            </span>
                          )}
                        </div>
                        <div style={{ color: 'var(--text-primary)', marginTop: '0.15rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {r.narration || '—'}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPendingDelete(r)}
                        title="Delete entry"
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--danger)', padding: '0.25rem',
                          display: 'flex', alignItems: 'center',
                        }}
                      >
                        <IconSystem.trash size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={discardOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete Time Entry"
        message={pendingDelete
          ? `Delete the ${formatHM(pendingDelete.hours)} entry from ${pendingDelete.entryDate?.slice(0, 10)}?`
          : ''}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleting}
        loadingMessage="Deleting…"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
};

export default AdhocLogModal;
